// src/pages/EditProfile.jsx
import React, { useEffect, useState, useRef } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import { Calendar } from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import DateObject from 'react-date-object';
import 'react-multi-date-picker/styles/layouts/mobile.css';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar as CalendarIcon, ArrowLeft } from 'lucide-react'; // درست
import Swal from 'sweetalert2';

export default function EditProfile() {
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    birth_date: '',
  });
  const [showCalendar, setShowCalendar] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const calendarRef = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  // ریسپانسیو
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // بستن تقویم با کلیک خارج
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // بارگذاری اطلاعات کاربر
  useEffect(() => {
    if (!token) {
      navigate('/login_page');
      return;
    }

    const fetchProfile = async () => {
      try {
        const res = await api.get('/api/users/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(res.data);
        setFormData({
          first_name: res.data.user.first_name || '',
          last_name: res.data.user.last_name || '',
          birth_date: res.data.user.birth_date || '',
        });
      } catch (err) {
        Swal.fire({
          icon: 'error',
          text: 'خطا در دریافت اطلاعات',
          toast: true,
          position: 'top',
          timer: 3000,
          showConfirmButton: false,
        });
        if (err.response?.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          navigate('/login_page');
        }
      }
    };

    fetchProfile();
  }, [token, navigate]);

  // تبدیل اعداد فارسی/عربی به لاتین
  const convertToLatinDigits = (input) => {
    if (!input) return input;
    const persianDigits = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g];
    const arabicDigits = [/٠/g, /١/g, /٢/g, /٣/g, /٤/g, /٥/g, /٦/g, /٧/g, /٨/g, /٩/g];
    let result = input;
    for (let i = 0; i < 10; i++) {
      result = result.replace(persianDigits[i], i).replace(arabicDigits[i], i);
    }
    return result;
  };

  // فرمت تاریخ برای بک‌اند
  const formatDateForBackend = (date) => {
    if (!date) return date;
    const latinDate = convertToLatinDigits(date);
    const parts = latinDate.split('/');
    if (parts.length === 3) {
      const [year, month, day] = parts.map(p => p.padStart(2, '0'));
      return `${year}/${month}/${day}`;
    }
    return latinDate;
  };

  // اعتبارسنجی تاریخ تولد
  const validateBirthDate = (value) => {
    if (!value) return false;
    const latinValue = convertToLatinDigits(value);
    const regex = /^\d{4}\/(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])$/;
    if (!regex.test(latinValue)) return false;
    const [year] = latinValue.split('/').map(Number);
    const currentYear = new Date().getFullYear();
    return year >= 1300 && year <= currentYear - 18;
  };

  // مدیریت ورودی‌ها
  const handleChange = (e) => {
    const { name, value } = e.target;
    const processedValue = name === 'birth_date' ? convertToLatinDigits(value) : value;
    setFormData({ ...formData, [name]: processedValue });
  };

  // تغییر تاریخ در تقویم
  const handleDateChange = (date) => {
    if (date) {
      const formatted = date.format('YYYY/MM/DD');
      setFormData({ ...formData, birth_date: formatted });
    } else {
      setFormData({ ...formData, birth_date: '' });
    }
    setShowCalendar(false);
  };

  // مقدار تقویم
  const getCalendarValue = () => {
    try {
      if (formData.birth_date && validateBirthDate(formData.birth_date)) {
        return new DateObject({
          date: convertToLatinDigits(formData.birth_date),
          calendar: persian,
          locale: persian_fa,
          format: 'YYYY/MM/DD',
        });
      }
      return null;
    } catch {
      return null;
    }
  };

  // ارسال فرم
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateBirthDate(formData.birth_date)) {
      Swal.fire({
        icon: 'error',
        text: 'تاریخ تولد نامعتبر است (مثال: 1370/01/01)',
        toast: true,
        position: 'top',
        timer: 3000,
        showConfirmButton: false,
      });
      return;
    }

    try {
      await api.put(
        '/api/users/update',
        { ...formData, birth_date: formatDateForBackend(formData.birth_date) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      Swal.fire({
        icon: 'success',
        text: 'اطلاعات با موفقیت بروزرسانی شد',
        toast: true,
        position: 'top',
        timer: 3000,
        showConfirmButton: false,
      });

      setTimeout(() => navigate('/profile_page'), 2000);
    } catch (err) {
      const msg = err.response?.data?.message || 'خطا در بروزرسانی';
      Swal.fire({
        icon: 'error',
        text: msg,
        toast: true,
        position: 'top',
        timer: 3000,
        showConfirmButton: false,
      });
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4 py-8 font-vazir">
      <div className="max-w-md mx-auto">
        {/* برگشت */}
        <motion.button
          whileHover={{ x: -5 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/profile_page')}
          className="flex items-center gap-2 text-indigo-600 mb-6 text-sm font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          برگشت به پروفایل
        </motion.button>

        {/* کارت اصلی */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-6 md:p-8 border border-white/30"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg">
              {formData.first_name?.[0] || 'پ'}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">ویرایش پروفایل</h2>
              <p className="text-sm text-gray-500">اطلاعات خود را بروز کنید</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* نام */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <label className="block text-sm font-medium text-gray-700 mb-1">نام</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 placeholder-gray-400"
                placeholder="نام خود را وارد کنید"
                required
              />
            </motion.div>

            {/* نام خانوادگی */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <label className="block text-sm font-medium text-gray-700 mb-1">نام خانوادگی</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 placeholder-gray-400"
                placeholder="نام خانوادگی خود را وارد کنید"
                required
              />
            </motion.div>

            {/* تاریخ تولد */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="relative"
            >
              <label className="block text-sm font-medium text-gray-700 mb-1">تاریخ تولد</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  name="birth_date"
                  value={formData.birth_date}
                  onChange={handleChange}
                  className="flex-1 px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 placeholder-gray-400"
                  placeholder="مثلاً 1370/01/01"
                  required
                />
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  type="button"
                  onClick={() => setShowCalendar(!showCalendar)}
                  className="p-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl shadow-lg"
                >
                  <CalendarIcon className="w-5 h-5" />
                </motion.button>
              </div>

              <AnimatePresence>
                {showCalendar && (
                  <motion.div
                    ref={calendarRef}
                    initial={{ opacity: 0, scale: 0.9, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: -10 }}
                    className="absolute z-50 mt-2 right-0 bg-white rounded-2xl shadow-2xl p-4 border border-gray-100"
                  >
                    <Calendar
                      calendar={persian}
                      locale={persian_fa}
                      value={getCalendarValue()}
                      onChange={handleDateChange}
                      className="rmdp-mobile"
                      mapDays={({ date }) => {
                        const isWeekend = [6].includes(date.weekDay.index);
                        if (isWeekend) return { disabled: true, style: { color: "#ccc" } };
                      }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* شماره تلفن و کد ملی */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">شماره تلفن</label>
                <input
                  type="text"
                  value={user.user.phone_number}
                  disabled
                  className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-xl text-gray-600 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">کد ملی</label>
                <input
                  type="text"
                  value={user.user.national_id}
                  disabled
                  className="w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-xl text-gray-600 cursor-not-allowed"
                />
              </div>
            </motion.div>

            {/* دکمه ثبت */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="w-full mt-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
            >
              <span>تایید و ثبت اطلاعات</span>
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              >
                <ArrowLeft className="w-5 h-5" />
              </motion.div>
            </motion.button>
          </form>
        </motion.div>

        <div className="text-center mt-8 text-xs text-gray-500">
          <p>© ۱۴۰۴ - ESF.Gold</p>
        </div>
      </div>
    </div>
  );
}








